<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { MediaRenderableTrack } from '../jitsi/runtime';

const props = defineProps<{
  class?: string;
  track: MediaRenderableTrack;
}>();

const element = ref<HTMLMediaElement | null>(null);

function attachTrack(target: HTMLMediaElement | null = element.value): void {
  if (!target) {
    return;
  }

  props.track.track.attach(target);
}

function detachTrack(target: HTMLMediaElement | null = element.value): void {
  if (!target) {
    return;
  }

  props.track.track.detach(target);
}

onMounted(() => {
  attachTrack();
});

onBeforeUnmount(() => {
  detachTrack();
});

watch(
  () => [props.track.id, props.track.kind, props.track.track] as const,
  () => {
    detachTrack();
    attachTrack();
  },
  { flush: 'post' },
);

watch(element, (nextElement, previousElement) => {
  if (previousElement && previousElement !== nextElement) {
    detachTrack(previousElement);
  }

  if (nextElement && nextElement !== previousElement) {
    attachTrack(nextElement);
  }
});
</script>

<template>
  <audio v-if="track.kind === 'audio'" ref="element" autoplay :class="props.class" :muted="track.muted" />

  <video
    v-else
    ref="element"
    autoplay
    playsinline
    :class="[props.class, track.mirrored ? '-scale-x-100' : '']"
    :muted="track.muted"
  />
</template>
